import React, { useEffect, useMemo, useState } from "react";

import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import { SlashCircle } from "@hl/base-components/lib/assets/icons.generated";
import {
  SwitchVertical01,
  SwitchVertical02,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows";
import {
  Cryptocurrency02,
  Diamond01,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Finance & eCommerce";
import { Hourglass01 } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Time";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import {
  TEXT_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import {
  WEIGHT_BOLD,
  WEIGHT_NORMAL,
} from "@hl/base-components/lib/theme/typography";
import {
  OrderSortOptions,
  SortDirection,
} from "@hl/shared-features/lib/apollo/graphql.generated";
import { NoResults } from "@hl/shared-features/lib/features/collections/NoResults";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import ListingSource from "@hl/shared-features/lib/features/marketplace/components/ListingSource";
import MarketplaceFilterSortBar from "@hl/shared-features/lib/features/marketplace/components/MarketplaceFilterSortBar";
import MarketplacePrice from "@hl/shared-features/lib/features/marketplace/components/MarketplacePrice";
import CancelListingModal, {
  CancelListingModalTitle,
} from "@hl/shared-features/lib/features/marketplace/modals/CancelListingModal";
import ManageOffersModal from "@hl/shared-features/lib/features/marketplace/modals/ManageOffersModal";
import { useUserListingsQuery } from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import { UserListing } from "@hl/shared-features/lib/features/marketplace/utils";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import usePercentDifference from "@hl/shared-features/lib/hooks/usePercentDifference";
import { SortingOption } from "@hl/shared-features/lib/utils/sortingOptions";
import {
  Anchor,
  Box,
  Button,
  createStyles,
  Grid,
  Group,
  Stack,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs, { extend } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfiniteScroll from "react-infinite-scroll-component";

import { getDetailPageUrl, getMintPageUrl } from "~config";

import { OnLoadProps } from "../profile/UserTabs";

import { Loading } from "./Collected";

extend(relativeTime);
extend(advancedFormat);
extend(utc);
extend(timezone);

const useStyles = createStyles({
  link: {
    textDecoration: "none",
  },
});

const sortingOptions: SortingOption<OrderSortOptions>[] = [
  {
    value: "exp_asc",
    label: "Expiring soonest",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.ASC,
    icon: Cryptocurrency02,
  },
  {
    value: "exp_desc",
    label: "Expiring latest",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.DESC,
    icon: Hourglass01,
  },
  {
    value: "date_desc",
    label: "Lowest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.ASC,
    icon: SwitchVertical02,
  },
  {
    value: "price_desc",
    label: "Highest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.DESC,
    icon: SwitchVertical01,
  },
  {
    value: "highest_above_floor",
    label: "Highest above floor",
    sortBy: OrderSortOptions.HIGHEST_ABOVE_FLOOR,
    sortDirection: SortDirection.DESC,
    icon: Diamond01,
  },
];

export const Listings = ({ onLoad }: OnLoadProps) => {
  const theme = useMantineTheme();
  const [searchString, setSearchString] = useState<string>("");
  const [debouncedSearchString] = useDebouncedValue(searchString, 250);
  const [sorting, setSorting] = useState<string | null>(
    sortingOptions[0].value
  );
  const selectedSort = useMemo(
    () =>
      sortingOptions.find((sortingOption) => sortingOption.value === sorting),
    [sorting]
  );
  const { data, fetchMore, loading, error } = useUserListingsQuery({
    variables: {
      tokenOrCollectionName: debouncedSearchString,
      sortBy: selectedSort!.sortBy,
      sortDirection: selectedSort!.sortDirection,
      after: null,
      first: 10,
    },
  });

  useEffect(() => {
    const totalCount = data?.getUserListings.pageInfo.totalCount;
    if (totalCount != null) {
      onLoad?.({ totalCount });
    }
  }, [data?.getUserListings.pageInfo.totalCount]);

  const nextCursor = data?.getUserListings?.pageInfo.endCursor;
  const hasNextPage = data?.getUserListings?.pageInfo?.hasNextPage;

  const listings: UserListing[] = useMemo(
    () => data?.getUserListings.edges || [],
    [data]
  );

  const receivedOffersTableHeaders = (
    <tr>
      <th>Item / Collection</th>
      <th>Listed Price</th>
      <th>Highest Offer</th>
      <th># of Offers</th>
      <th>Expires In</th>
      <th>Marketplace</th>
      <th></th>
    </tr>
  );

  const receivedOffersTableRows = listings.map((listing, i) => (
    <ListingRow listing={listing} key={i} />
  ));

  return (
    <Stack>
      <MarketplaceFilterSortBar
        setSorting={setSorting}
        sorting={sorting}
        hasFilterToggle={false}
        sortingOptions={sortingOptions}
        setSearchString={setSearchString}
        searchString={searchString}
      />
      {loading ? (
        <Loading />
      ) : listings.length ? (
        <InfiniteScroll
          dataLength={listings.length}
          next={() =>
            hasNextPage &&
            fetchMore({
              variables: { after: nextCursor },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                fetchMoreResult!.getUserListings.edges = [
                  ...(previousResult?.getUserListings?.edges ?? []),
                  ...(fetchMoreResult?.getUserListings?.edges ?? []),
                ];
                return fetchMoreResult;
              },
            })
          }
          hasMore={!!hasNextPage}
          loader={<Loading />}
        >
          <Table
            verticalSpacing="md"
            miw={1200}
            sx={{
              "thead tr th": {
                color: theme.colors.secondaryText[0],
                paddingLeft: 0,
                fontSize: 14,
                fontWeight: WEIGHT_NORMAL,
                paddingTop: 8,
              },
              "tbody tr td": {
                paddingLeft: 0,
              },
              "tbody tr td:last-child": {
                paddingRight: 0,
              },
            }}
          >
            <thead>{receivedOffersTableHeaders}</thead>
            <tbody>{receivedOffersTableRows}</tbody>
          </Table>
        </InfiniteScroll>
      ) : !!error ? (
        <NoResults
          title="Something went wrong"
          description="Please try again later"
        />
      ) : (
        <NoResults
          description={
            debouncedSearchString
              ? "Try adjusting your filter settings"
              : "Once you do, they'll appear here."
          }
          title={
            debouncedSearchString
              ? "No listings found"
              : "No listing created yet"
          }
        />
      )}
    </Stack>
  );
};

function ListingRow({ listing }: { listing: UserListing }) {
  const theme = useMantineTheme();
  const { pushModal } = useModalStack();
  const { classes } = useStyles();
  const {
    token,
    collection,
    price,
    floorPrice,
    highestOffer,
    offersCount,
    quantityRemaining,
  } = listing;
  const { diffWording } = usePercentDifference(
    price.amount.native,
    floorPrice,
    true
  );
  const {
    isDiffPositive: isDiffHighestOfferPositive,
    diffWording: diffHighestOfferWording,
  } = usePercentDifference(highestOffer?.price.amount.native, floorPrice);
  const imageUrl = token?.imageUrl
    ? token.imageUrl
    : collection?.imageUrl ?? "";
  const collectionUrl = collection ? getMintPageUrl(collection) : "";
  const tokenUrl =
    collection && token ? getDetailPageUrl(collection, token.id) : "";
  const fp = floorPrice && floorPrice != "0" ? parseFloat(floorPrice) : null;
  const tokenName = token?.name || (token?.id ? `#${token.id}` : null);
  const collectionName = collection?.name || null;

  return (
    <tr key={listing.createdAt}>
      <td>
        <Group noWrap>
          <Box p={8} bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}>
            <ResizedImage src={imageUrl} width={64} height={64} fit="contain" />
          </Box>
          <Stack spacing={4}>
            <Group spacing="xs">
              <Anchor href={tokenUrl} className={classes.link}>
                <Text fw={WEIGHT_BOLD}>{tokenName || "Unknown token"}</Text>
              </Anchor>
              {quantityRemaining > 1 && <Text>x{quantityRemaining}</Text>}
            </Group>
            <Anchor href={collectionUrl} className={classes.link}>
              <Text size="sm">{collectionName || "Unknown collection"}</Text>
            </Anchor>
          </Stack>
        </Group>
      </td>
      <td>
        <Stack spacing={4}>
          <MarketplacePrice size="md" price={listing.price} />
          {fp && (
            <Text size="sm" color={TEXT_COLOR.SECONDARY}>
              <FormatCrypto
                amount={fp}
                onlyText
                symbol={listing.price.currency.symbol}
                decimals={listing.price.currency.decimals}
              />{" "}
              floor ({diffWording})
            </Text>
          )}
        </Stack>
      </td>
      <td>
        <Stack spacing={4}>
          <MarketplacePrice size="md" price={highestOffer?.price} />
          <Text
            size="sm"
            color={
              isDiffHighestOfferPositive
                ? theme.colors.affirmativeStatus[0]
                : theme.colors.warningStatus[0]
            }
          >
            {diffHighestOfferWording}
          </Text>
        </Stack>
      </td>
      <td>
        <Text size="md">{offersCount}</Text>
      </td>
      <td>
        {listing.expiredAt && !listing.source?.includes("blur") && (
          <Stack spacing={4}>
            <Text size="md">
              {dayjs(listing.expiredAt).format("MMM. DD @ h:mma z")}
            </Text>
            <Text size="sm" color={TEXT_COLOR.SECONDARY}>
              {dayjs().to(listing.expiredAt)}
            </Text>
          </Stack>
        )}{" "}
      </td>
      <td>
        {/*<Anchor href={listing.source ?? ""} target="_blank" rel="noreferrer">*/}
        <ListingSource
          domain={listing.source ?? ""}
          onlyForeign={false}
          size="md"
          color={TEXT_COLOR.SECONDARY}
          isShort={true}
        />
        {/*</Anchor>*/}
      </td>
      <td align="right">
        <Grid w={336} gutter={0}>
          <Grid.Col span={6} pr={6}>
            <Button
              w="100%"
              size="lg"
              color={OUTLINE_COLOR}
              px={12}
              onClick={() =>
                !!collection &&
                pushModal(
                  <CancelListingModal
                    collectionData={{ ...collection, chainId: listing.chainId }}
                    tokenId={token?.id}
                    id={listing.reservoirId}
                    imageUrl={imageUrl ?? collection.imageUrl}
                    floorPrice={listing.floorPrice}
                  />,
                  { size: "lg", title: CancelListingModalTitle() }
                )
              }
            >
              <Group spacing={8}>
                <SlashCircle width={16} />
                Cancel listing
              </Group>
            </Button>
          </Grid.Col>
          <Grid.Col span={6} pl={6}>
            <Button
              w="100%"
              size="lg"
              onClick={() =>
                pushModal(
                  <ManageOffersModal
                    collectionData={{
                      id: collection?.id,
                      onchainId: collection?.onchainId ?? null,
                      address: collection?.address || "",
                      chainId: listing?.chainId,
                      name: collection?.name || "",
                      standard: collection?.standard,
                    }}
                    tokenId={token?.id}
                    onCompleted={() => ({})}
                  />,
                  {
                    size: "100%",
                    title: (
                      <Text fw={WEIGHT_BOLD}>
                        Manage offers for {token?.name} · {collection?.name}
                      </Text>
                    ),
                  }
                )
              }
              color={SECONDARY_COLOR}
              disabled={!offersCount}
              px={8}
            >
              {!!offersCount
                ? `View ${offersCount > 2 ? "all " : ""}${offersCount} offer${
                    offersCount > 1 ? "s" : ""
                  }`
                : "No offers"}
            </Button>
          </Grid.Col>
        </Grid>
      </td>
    </tr>
  );
}
